// ============================================================
// ExpressDockerCloudDeployEC2.js
//
// A branded, responsive course content page for the
// "Automated EC2 Deployment" lesson.
//
// Author: ProDev Linux Series (DevOps Branch)
// Features: SSH Action integration, GitHub Secret mapping,
//           and automated container lifecycle management.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import CloudSyncIcon          from "@mui/icons-material/CloudSync";
import TerminalIcon           from "@mui/icons-material/Terminal";
import SecurityIcon           from "@mui/icons-material/Security";
import GitHubIcon             from "@mui/icons-material/GitHub";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import StorageIcon            from "@mui/icons-material/Storage";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressDockerCloudDeployEC2() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Module 33"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Automated <span>EC2 Deployment</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Close the loop on your CI/CD pipeline. By using GitHub Actions to SSH into your EC2 instance, you can pull the latest Docker image and restart your services automatically.
                    </Typography>
                </Box>

                {/* 1. EC2 PREP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Preparing the EC2 Target" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Docker Runtime", desc: "Install Docker and ensure the user has permissions to run commands without sudo." },
                            { title: "Security Groups", desc: "Open Port 22 (SSH) for GitHub's IP range and your App Port (e.g., 4000)." },
                            { title: "SSH Access", desc: "Generate a dedicated SSH Key Pair; keep the Private Key (.pem) for GitHub." },
                        ].map((item, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 13, mb: 1 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 2. GITHUB SECRETS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="2. Required GitHub Secrets" />
                    <CodeBlock lines={[
                        { cmd: "HOST", comment: "The EC2 Public DNS or IPv4" },
                        { cmd: "USERNAME", comment: "Typically 'ubuntu' or 'ec2-user'" },
                        { cmd: "SSH_PRIVATE_KEY", comment: "The full contents of your .pem file" },
                    ]} />
                </Box>

                {/* 3. DEPLOYMENT WORKFLOW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudSyncIcon />} label="3. The Deployment Workflow" />
                    <CodeBlock lines={[
                        { cmd: "- name: Deploy to EC2 via SSH", comment: "Uses appleboy/ssh-action" },
                        { cmd: "  uses: appleboy/ssh-action@v1", comment: "" },
                        { cmd: "  with:", comment: "" },
                        { cmd: "    host: ${{ secrets.HOST }}", comment: "" },
                        { cmd: "    username: ${{ secrets.USERNAME }}", comment: "" },
                        { cmd: "    key: ${{ secrets.SSH_PRIVATE_KEY }}", comment: "" },
                        { cmd: "    script: |", comment: "Remote shell commands" },
                        { cmd: "      docker pull username/my-app:latest", comment: "" },
                        { cmd: "      docker stop myapp || true", comment: "" },
                        { cmd: "      docker rm myapp || true", comment: "" },
                        { cmd: "      docker run -d --name myapp -p 4000:4000 username/my-app:latest", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Operational Best Practices" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Pruning", desc: "Run 'docker system prune -f' in your script to clean up old layers.", icon: <StorageIcon /> },
                            { title: "Health Checks", desc: "Add a sleep and a curl command to verify the app is actually up.", icon: <CheckCircleOutlineIcon /> },
                            { title: "Zero Downtime", desc: "Consider using Nginx as a reverse proxy for blue-green deployments.", icon: <CodeIcon /> },
                        ].map((item, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Stack>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> For production, use the unique <code style={{ color: BRAND.goldBright }}>github.sha</code> as a tag instead of <code style={{ color: BRAND.goldBright }}>latest</code>. This ensures you know exactly which version is deployed and allows for instant rollbacks.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
